import type { AxisSelection } from '@atlas/types'
import { RuleValidationTargetType } from '@atlas/types'
import { CardShell, ListSkeleton } from '@atlas/ui-components'
import { Button } from '@atlas/ui-primitives'
import { useId, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toRuleListItems } from '@/adapters/rule-list-item'
import { CampaignBreadcrumb } from '@/components/campaigns/CampaignBreadcrumb'
import { CampaignFileGallery } from '@/components/campaigns/CampaignFileGallery'
import { CampaignFilePickerDialog } from '@/components/campaigns/CampaignFilePickerDialog'
import { CampaignLifecyclePanel } from '@/components/campaigns/CampaignLifecyclePanel'
import { CampaignMockupSlots } from '@/components/campaigns/CampaignMockupSlots'
import { CampaignStatusBadge } from '@/components/campaigns/CampaignStatusBadge'
import { CampaignUploadDialog } from '@/components/campaigns/CampaignUploadDialog'
import { CAMPAIGN_MOCKUP_SLOT_DECOR } from '@/components/campaigns/campaign-mockup-examples'
import { ConfirmDialog } from '@/components/confirm/ConfirmDialog'
import { ApplicableRulesTabs } from '@/components/rules/ApplicableRulesTabs'
import type { CampaignStatus } from '@/domain/campaign.types'
import { useAuth } from '@/hooks/useAuth'
import { useApplicableRules } from '@/hooks/useExplore'
import { useRuleEditing } from '@/hooks/useRuleEditing'
import { CampaignDetailMessagePicker, CampaignDetailMessages } from './CampaignDetailMessages'
import { useCampaignDetailContainer } from './useCampaignDetailContainer'
import { useCampaignFilesSectionContainer } from './useCampaignFilesSectionContainer'
import { useCampaignMessageMatrixContainer } from './useCampaignMessageMatrixContainer'

type FilesSection = ReturnType<typeof useCampaignFilesSectionContainer>

/** The picker and upload dialogs, extracted to keep the page component short. */
function CampaignDetailFilesDialogs({ filesSection }: { filesSection: FilesSection }) {
	return (
		<>
			<CampaignFilePickerDialog
				isOpen={filesSection.isPickerOpen}
				candidates={filesSection.pickerCandidates}
				isLoading={filesSection.isPickerLoading}
				isError={filesSection.isPickerError}
				search={filesSection.pickerSearch}
				onSearchChange={filesSection.onPickerSearchChange}
				selectedIds={filesSection.pickerSelectedIds}
				onToggleSelection={filesSection.togglePickerSelection}
				isAssociationPending={filesSection.isAssociationPending}
				isAssociationError={filesSection.isAssociationError}
				isAssociationSuccess={filesSection.isAssociationSuccess}
				onConfirm={filesSection.onConfirmPicker}
				onRetry={filesSection.onRetryPickerAssociation}
				onCancel={filesSection.onClosePicker}
			/>
			<CampaignUploadDialog
				isOpen={filesSection.isUploadOpen}
				transientState={filesSection.uploadTransientState}
				outcome={filesSection.uploadOutcome}
				isPending={filesSection.isUploadPending}
				progress={filesSection.uploadProgress}
				onStartUpload={filesSection.onStartUpload}
				onRetryAssociation={filesSection.onRetryUploadAssociation}
				onResetUploadPresentation={filesSection.onResetUploadPresentation}
				onCancel={filesSection.onCloseUpload}
			/>
		</>
	)
}

/**
 * Applicable rules and their recorded validation results for the campaign, tabbed the
 * same way every other surface with a validation target shows them (`ApplicableRulesTabs`).
 * Its own component so the rules hooks run only once the campaign - and its classification
 * `selection` - is known, rather than juggling an `enabled`/fallback tuple in the page body.
 */
function CampaignRulesSection({ campaignId, selection }: { campaignId: string; selection: AxisSelection }) {
	const rulesRead = useApplicableRules(selection)
	const { canEdit, editRule, ruleHref, openRule } = useRuleEditing()

	return (
		<ApplicableRulesTabs
			targetType={RuleValidationTargetType.CAMPAIGN}
			targetId={campaignId}
			groupBySubject
			rules={toRuleListItems(rulesRead.data ?? [])}
			isLoading={rulesRead.isLoading}
			isError={rulesRead.isError}
			canEdit={canEdit}
			onEditRule={editRule}
			ruleHref={ruleHref}
			onOpenRule={openRule}
			emptyLabel="No rules resolve for this campaign's context."
			note={canEdit ? 'Editing a rule changes it everywhere it resolves, not only on this campaign.' : undefined}
		/>
	)
}

type CampaignDetailSection = ReturnType<typeof useCampaignDetailContainer>

/**
 * The detail heading area: the Campaign's identity and current status, with the
 * compact lifecycle actions and the capability-gated Edit and Delete alongside
 * them. Every supplied lifecycle action stays perceivable here without opening a
 * menu, popover, or disclosure, and the controls wrap at narrow widths.
 */
function CampaignDetailHeading({
	campaign,
	detail,
	selectedNextStatus,
	onSelectNextStatus,
	onEdit,
}: {
	campaign: NonNullable<CampaignDetailSection['campaign']>
	detail: CampaignDetailSection
	selectedNextStatus: CampaignStatus | null
	onSelectNextStatus: (status: CampaignStatus) => void
	onEdit: () => void
}) {
	return (
		<div className="flex flex-wrap items-start justify-between gap-4">
			<div className="min-w-0">
				<p className="text-xs font-medium uppercase tracking-widest text-ds-text-subtle">{`CAMPAIGN · ${campaign.campaignCode}`}</p>
				<h1 className="mt-1 min-w-0 break-words font-ds-display text-3xl font-semibold text-ds-text">
					{campaign.name}
				</h1>
			</div>
			<div className="flex min-w-0 flex-wrap items-center gap-3">
				<CampaignLifecyclePanel
					status={campaign.status}
					availableActions={detail.availableActions}
					canTransition={detail.capabilities.canTransition}
					viewState={detail.transitionViewState}
					selectedNextStatus={selectedNextStatus}
					onSelectNextStatus={onSelectNextStatus}
					onSubmitTransition={() => selectedNextStatus && detail.onTransition(selectedNextStatus)}
					onReload={detail.onReloadCampaign}
				/>
				{(detail.capabilities.canEdit || detail.capabilities.canDelete) && (
					<div className="flex flex-wrap gap-2 border-l border-ds-border pl-3">
						{detail.capabilities.canEdit && <Button onPress={onEdit}>Edit campaign</Button>}
						{detail.capabilities.canDelete && (
							<Button variant="secondary" onPress={detail.onOpenDeleteDialog}>
								Delete
							</Button>
						)}
					</div>
				)}
			</div>
		</div>
	)
}

/**
 * One compact metadata chip: a label/value pair, kept as its own `dt`+`dd` group
 * for `<dl>` semantics.
 *
 * Not the `Chip` primitive: that renders a `<span>` around its children, which
 * cannot carry a `dt`/`dd` pair without breaking the definition list.
 */
function CampaignMetadataChip({ label, value }: { label: string; value: string }) {
	return (
		<div className="flex min-w-0 items-center gap-1 rounded-ds-full border border-ds-border bg-ds-surface-elevated px-3 py-1">
			<dt className="text-xs font-medium uppercase tracking-wide text-ds-text-subtle">{label}</dt>
			<span aria-hidden="true" className="text-ds-text-subtle">
				·
			</span>
			<dd className="min-w-0 truncate text-sm text-ds-text" title={value}>
				{value}
			</dd>
		</div>
	)
}

/**
 * Compact metadata: Window, Geography, the current status, and Veeva code form
 * a primary wrapping row; Objective and Brand form a secondary row. Optional
 * fields render only when present — no placeholder for an absent value. Status
 * always renders, since it is a required field, using the existing status badge.
 */
function CampaignOptionalMetadata({
	windowLabel,
	geographyLabel,
	objectiveLabel,
	brandLabel,
	veevaCode,
	status,
}: {
	windowLabel: string | null
	geographyLabel: string | undefined
	objectiveLabel: string | undefined
	brandLabel: string | undefined
	veevaCode: string | null
	status: CampaignStatus
}) {
	return (
		<dl className="space-y-2">
			<div className="flex flex-wrap items-center gap-2">
				{windowLabel !== null && <CampaignMetadataChip label="Window" value={windowLabel} />}
				{geographyLabel !== undefined && <CampaignMetadataChip label="Geography" value={geographyLabel} />}
				<div className="flex min-w-0 items-center gap-1.5">
					<dt className="text-xs font-medium uppercase tracking-wide text-ds-text-subtle">Status</dt>
					<dd>
						<CampaignStatusBadge status={status} />
					</dd>
				</div>
				{veevaCode !== null && <CampaignMetadataChip label="Veeva" value={veevaCode} />}
			</div>
			{(objectiveLabel !== undefined || brandLabel !== undefined) && (
				<div className="flex flex-wrap items-center gap-2">
					{objectiveLabel !== undefined && <CampaignMetadataChip label="Objective" value={objectiveLabel} />}
					{brandLabel !== undefined && <CampaignMetadataChip label="Brand" value={brandLabel} />}
				</div>
			)}
		</dl>
	)
}

/**
 * Campaign detail — the detail container. Breadcrumb, metadata (optional
 * fields rendered only when present), the lifecycle panel driven by the
 * lifecycle port's data, the associated-Files section, and the four mockup
 * placeholders. Every mutation affordance is capability-gated.
 */
export function CampaignDetailPage() {
	const { campaignId = '' } = useParams<{ campaignId: string }>()
	const navigate = useNavigate()
	const { user } = useAuth()
	const [selectedNextStatus, setSelectedNextStatus] = useState<CampaignStatus | null>(null)
	const aboutHeadingId = useId()

	const detail = useCampaignDetailContainer(campaignId, user?.role ?? '', () => void navigate('/campaigns'))
	const filesSection = useCampaignFilesSectionContainer(campaignId)
	// Called unconditionally (before the loading/error guards below) per Rules of
	// Hooks — the Campaign itself is unknown until `detail.campaign` resolves, and
	// `useCampaignMessages`'s `enabled: !!code` already treats the absent code as
	// "nothing to fetch yet," matching `campaignId = ''`'s own default above.
	const messagesSection = useCampaignMessageMatrixContainer(campaignId, detail.campaign)

	if (detail.isLoading) return <ListSkeleton label="Loading…" />
	if (detail.isNotFound) return <p className="text-sm text-ds-text-muted">Campaign not found.</p>
	if (detail.isError || !detail.campaign) {
		return (
			<p role="alert" className="text-sm text-ds-danger">
				Unable to load this Campaign.
			</p>
		)
	}

	const campaign = detail.campaign
	const geographyTag = campaign.classification.tags.find((tag) => tag.axisCode === 'geography')
	const objectiveTag = campaign.classification.tags.find((tag) => tag.axisCode === 'objective')
	const brandTag = campaign.classification.tags.find((tag) => tag.axisCode === 'brand')

	return (
		<div className="space-y-4">
			<CampaignBreadcrumb
				trail={[{ label: 'Campaigns', href: '/campaigns' }, { label: campaign.name }]}
				onNavigate={(href) => void navigate(href)}
			/>

			<CampaignDetailHeading
				campaign={campaign}
				detail={detail}
				selectedNextStatus={selectedNextStatus}
				onSelectNextStatus={setSelectedNextStatus}
				onEdit={() => void navigate(`/campaigns/${campaign.campaignId}/edit`)}
			/>

			<CampaignOptionalMetadata
				windowLabel={campaign.windowLabel}
				geographyLabel={geographyTag?.label}
				objectiveLabel={objectiveTag?.label}
				brandLabel={brandTag?.label}
				veevaCode={campaign.veevaCode}
				status={campaign.status}
			/>

			<CampaignMockupSlots slots={CAMPAIGN_MOCKUP_SLOT_DECOR} />

			{/* Omitted entirely — heading included — when the Campaign carries no description. */}
			{campaign.description !== null && (
				<CardShell as="section" aria-labelledby={aboutHeadingId} className="space-y-1.5 p-3">
					<h2 id={aboutHeadingId} className="text-xs font-medium uppercase tracking-widest text-ds-text-subtle">
						About this campaign
					</h2>
					<p className="text-sm leading-snug text-ds-text-muted">{campaign.description}</p>
				</CardShell>
			)}

			<CampaignFileGallery
				files={filesSection.files}
				isLoading={filesSection.isFilesLoading}
				isError={filesSection.isFilesError}
				selectedFileIds={filesSection.selectedFileIds}
				onToggleSelection={filesSection.toggleFileSelection}
				downloadCapabilities={filesSection.downloadCapabilities}
				onDownloadIndividual={filesSection.onDownloadIndividual}
				downloadingFileId={filesSection.downloadingFileId}
				thumbnailUrls={filesSection.thumbnailUrls}
				canAssociateFiles={detail.capabilities.canAssociateFiles}
				canUploadFiles={detail.capabilities.canUploadFiles}
				onBrowseExisting={filesSection.onOpenPicker}
				onUploadNew={filesSection.onOpenUpload}
				onDownloadSelected={filesSection.onDownloadSelected}
				onDownloadAll={filesSection.onDownloadAll}
				isBulkDownloadPending={filesSection.bulkDownloadKind !== null}
				bulkDownloadError={filesSection.bulkDownloadError ?? undefined}
			/>

			<CampaignDetailFilesDialogs filesSection={filesSection} />

			<CampaignDetailMessages messagesSection={messagesSection} capabilities={detail.capabilities} />

			<CampaignRulesSection campaignId={campaignId} selection={campaign.classification.selection} />

			<CampaignDetailMessagePicker picker={messagesSection.picker} />

			<ConfirmDialog
				open={detail.isDeleteDialogOpen}
				title="Delete this Campaign?"
				description="This cannot be undone."
				confirmLabel="Delete"
				confirmVariant="danger"
				submitting={detail.deleteViewState === 'pending'}
				busyLabel="Working…"
				errorMessage={detail.deleteViewState === 'failure' ? 'Unable to delete this Campaign.' : undefined}
				onConfirm={detail.onConfirmDelete}
				onCancel={detail.onCancelDelete}
			/>
		</div>
	)
}
